import {
  IBulkUpdateProjectComponentDrawingNumber,
  ICagePositionMinimalData,
  IComponent,
  IComponentWithPositions,
  IPosition,
  IPositionWithDrawing,
  IUpdateProjectComponentDrawingNumber,
} from 'src/generated-types'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { queryClient } from 'src/query-client'
import { projectPositionQueryKeys } from 'src/query-keys'
import { ISetMblData } from 'src/generated-types/set-mbl'
import { RemoveComponentData } from 'src/generated-types/remove-component'
import { IAddWorkData } from 'src/generated-types/add-work'
import { AddComponentDataWithPositions } from 'src/generated-types/add-component'
import { IDeleteWorkData, IWork, IWorkWithPosition } from 'src/generated-types/work'
import { ComponentBulkUpdateData } from 'src/generated-types/component-bulk-update'
import { OverrideComponentData } from 'src/generated-types/override-component'
import { IComponentNote } from 'src/generated-types/component-note'
import { IUpdateGroupedPositionComponents } from 'src/generated-types/component-update-grouped-positions'
import { IPositionComponentUpdateData } from 'src/generated-types/component-bulk-update-positions'
import {
  createPositionDrawingGroup,
  DrawingWithGroups,
  PositionDrawingGroup,
  PositionsWithGroups,
} from 'src/generated-types/position-drawing-group'
import { IConfigurePositionsData } from 'src/generated-types/configure-positions'
import { ComponentCount } from 'src/generated-types/component-count'
import { IApiResult } from 'src/generated-types/api-result'
import { IConfirmOlexPositions } from 'src/generated-types/confirm-olex'
import { projectComponentEndpoints } from './projectComponent'

const baseUrl = (customerId: EntityId, projectId: EntityId) => `customers/${customerId}/projects/${projectId}/positions`

export const projectPositionEndpoints = {
  createProjectPosition(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}`
  },
  getAllProjectPositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}`
  },
  getAllProjectPositionsOnSearchInput(customerId: EntityId, projectId: EntityId, searchInput: string) {
    return `${baseUrl(customerId, projectId)}?searchInput=${searchInput}`
  },
  getComponentsforPositions(
    customerId: EntityId,
    projectId: EntityId,
    positionIds: EntityId[],
    positionDrawingId: number,
  ) {
    return `${baseUrl(customerId, projectId)}/get-components-for-positions?positionIds=${encodeURIComponent(
      positionIds.join('&'),
    )}&positionDrawingId=${encodeURIComponent(positionDrawingId)}`
  },
  getComponentsForSinglePosition(customerId: EntityId, projectId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, projectId)}/${positionId}/get-components`
  },
  setMblValueForPositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/set-mbl`
  },
  removeComponents(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/remove-component`
  },
  addWorkToPositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/add-work`
  },
  addComponentForMultiplePositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/add-component-bulk`
  },
  deleteWork(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/delete-work`
  },
  getWorkItemsForPosition(customerId: EntityId, projectId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, projectId)}/${positionId}/get-work-items`
  },
  updateComponentForMultiplePositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/update-component-bulk`
  },
  overrideComponetsForPositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/override-components`
  },
  updateProjectPositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}`
  },
  getProjectPosition(customerId: EntityId, projectId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, projectId)}/${positionId}`
  },
  getMultipleProjectPositions(customerId: EntityId, projectId: EntityId, positionIds: EntityId[]) {
    return `${baseUrl(customerId, projectId)}/get-grouped-positions?positionIds=${encodeURIComponent(
      positionIds.join('&'),
    )}`
  },
  getComponentsByProductId(customerId: EntityId, projectId: EntityId, positionIds: EntityId[]) {
    return `${baseUrl(customerId, projectId)}/get-components-by-productId?positionIds=${encodeURIComponent(
      positionIds.join('&'),
    )}`
  },
  getComponentsForPositionIds(customerId: EntityId, projectId: EntityId, positionIds: EntityId[]) {
    return `${baseUrl(customerId, projectId)}/get-components-for-position-ids?positionIds=${encodeURIComponent(
      positionIds.join('&'),
    )}`
  },
  createComponentNotes(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/component/add-note`
  },
  updatePositionMinimalData(customerId: EntityId, projectId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, projectId)}/${id}/update-position-minimal-data`
  },
  updateGroupedPositionComponents(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/update-grouped-components`
  },
  updatePositionComponents(customerId: EntityId, projectId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, projectId)}/${positionId}/update-position-components`
  },
  deleteProjectPosition(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}`
  },
  updateComponentOnDrag(customerId: EntityId, projectId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, projectId)}/${positionId}/update-component-position-number`
  },
  bulkUpdateComponentsOnDrag(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/bulk-update-component-position-number`
  },
  addPositionDrawingGroup(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/add-position-group`
  },
  getPositionDrawingGroups(customerId: EntityId, projectId: EntityId, positionDrawingId: EntityId) {
    return `${baseUrl(customerId, projectId)}/${positionDrawingId}/get-position-groups`
  },
  unAssignPositionsFromGroup(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/unassign-positions`
  },
  updatePositionGroupName(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/update-position-group`
  },
  addNoteToGroup(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/add-note-to-group`
  },
  addPostitionsToGroup(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/add-positions-to-group`
  },
  changePositionDrawing(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/change-position-drawing`
  },
  getAllPositionDrawingGroups(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/get-all-position-drawing-groups`
  },
  getPositionsGroupedByDrawingGroups(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/get-positions-grouped-by-drawing-groups`
  },
  configurePositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/configure-positions`
  },
  deletePositionDrawingGroup(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/position-drawing-groups`
  },
  bulkConfigurePositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/bulk-configure-positions`
  },
  getProjectComponentCount(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/get-project-component-count`
  },
  getProjectWorkList(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/get-project-work-summary`
  },
  markAsCompleted(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/mark-as-completed`
  },
  markAsIncomplete(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/mark-as-incomplete`
  },
  markComponentAsReused(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/mark-component-as-reused`
  },
  confirmProjectPositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/confirm-positions`
  },
}

export const invalidateProjectPositionCache = {
  getProjectPositions(customerId: EntityId, projectId: EntityId, searchInput?: string) {
    queryClient.invalidateQueries(projectPositionQueryKeys.GET_POSITIONS(customerId, projectId, searchInput))
  },
  getComponentsForPositions(customerId: EntityId, projectId: EntityId, positionIds?: EntityId[]) {
    queryClient.invalidateQueries(
      projectPositionQueryKeys.GET_COMPONENTS_FOR_POSITIONS(customerId, projectId, positionIds),
    )
  },
  getWorkItemsForPosition(customerId: EntityId, projectId: EntityId, positionId: EntityId) {
    queryClient.invalidateQueries(
      projectPositionQueryKeys.GET_WORK_ITEMS_FOR_POSITION(customerId, projectId, positionId),
    )
  },
  getProjectPosition(customerId: EntityId, projectId: EntityId, positionId: EntityId) {
    queryClient.invalidateQueries(projectPositionQueryKeys.GET_POSITION(customerId, projectId, positionId))
  },
  getMultipleProjectPositions(customerId: EntityId, projectId: EntityId, positionIds: EntityId[]) {
    queryClient.invalidateQueries(projectPositionQueryKeys.GET_MULTIPLE_POSITIONS(customerId, projectId, positionIds))
  },
  getComponentsByProductId(
    customerId: EntityId,
    projectId: EntityId,
    positionIds: EntityId[],
    productId?: EntityId,
    positionNumber?: number,
  ) {
    queryClient.invalidateQueries(
      projectPositionQueryKeys.GET_COMPONENTS_BY_PRODUCT_ID(
        customerId,
        projectId,
        positionIds,
        productId,
        positionNumber,
      ),
    )
  },
  getComponentsForSinglePosition(customerId: EntityId, positionId: EntityId) {
    queryClient.invalidateQueries([projectComponentEndpoints.getComponents(customerId, positionId), 'with-prices'])
  },
  getPositionDrawingGroups(customerId: EntityId, projectId: EntityId, positionDrawingId?: EntityId) {
    queryClient.invalidateQueries(
      projectPositionQueryKeys.GET_POSITION_DRAWING_GROUPS(customerId, projectId, positionDrawingId),
    )
  },
  getAllPositionDrawingGroups(customerId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectPositionQueryKeys.GET_ALL_POSITION_DRAWING_GROUPS(customerId, projectId))
  },
  getPositionsGroupedByDrawingGroups(customerId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(
      projectPositionQueryKeys.GET_POSITIONS_GROUPED_BY_DRAWING_GROUPS(customerId, projectId),
    )
  },
  getProjectComponentCount(customerId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectPositionEndpoints.getProjectComponentCount(customerId, projectId))
  },
  getProjectWorkList(customerId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectPositionEndpoints.getProjectWorkList(customerId, projectId))
  },
}

export const useGetProjectPositions = (customerId: EntityId, projectId: EntityId, searchInput?: string) => {
  return useGet<IPositionWithDrawing[]>(
    searchInput
      ? projectPositionEndpoints.getAllProjectPositionsOnSearchInput(customerId, projectId, searchInput)
      : projectPositionEndpoints.getAllProjectPositions(customerId, projectId),
    {},
    {
      enabled: customerId != null && projectId != null,
      queryKey: projectPositionQueryKeys.GET_POSITIONS(customerId, projectId, searchInput),
    },
  )
}

export const useCreateProjectPosition = (customerId: EntityId, projectId: EntityId) =>
  usePost<IPosition, IPosition>(projectPositionEndpoints.createProjectPosition(customerId, projectId))

export const useGetComponentsForPositions = (
  customerId: EntityId,
  projectId: EntityId,
  positionIds: EntityId[],
  positionDrawingId: number,
  enabled: boolean,
) => {
  return useGet<IComponentWithPositions[]>(
    projectPositionEndpoints.getComponentsforPositions(customerId, projectId, positionIds, positionDrawingId),
    {},
    {
      enabled: positionIds.length > 0 && !!customerId && !!projectId && enabled,
      queryKey: projectPositionQueryKeys.GET_COMPONENTS_FOR_POSITIONS(customerId, projectId, positionIds),
    },
  )
}

export const useSetMblValueForPositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<IPosition, ISetMblData>(projectPositionEndpoints.setMblValueForPositions(customerId, projectId))

export const useAddWorkToPositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<IPosition, IAddWorkData>(projectPositionEndpoints.addWorkToPositions(customerId, projectId))

export const useRemoveComponents = (customerId: EntityId, projectId: EntityId) =>
  usePost<IPosition, RemoveComponentData>(projectPositionEndpoints.removeComponents(customerId, projectId))

export const useAddComponentForMultiplePositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<IPosition, AddComponentDataWithPositions>(
    projectPositionEndpoints.addComponentForMultiplePositions(customerId, projectId),
  )

export const useDeleteWork = (customerId: EntityId, projectId: EntityId) =>
  usePost<IPosition, IDeleteWorkData>(projectPositionEndpoints.deleteWork(customerId, projectId))

export const useGetWorkItemsForPosition = (customerId: EntityId, projectId: EntityId, positionId: EntityId) =>
  useGet<IWork[]>(
    projectPositionEndpoints.getWorkItemsForPosition(customerId, projectId, positionId),
    {},
    {
      queryKey: projectPositionQueryKeys.GET_WORK_ITEMS_FOR_POSITION(customerId, projectId, positionId),
      enabled: !!customerId && !!projectId && !!positionId,
    },
  )

export const useUpdateComponentForMultiplePositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<IComponent, ComponentBulkUpdateData>(
    projectPositionEndpoints.updateComponentForMultiplePositions(customerId, projectId),
  )

export const useUpdateProjectPositions = (customerId: EntityId, projectId: EntityId) =>
  usePut<IPosition, IPosition[]>(projectPositionEndpoints.updateProjectPositions(customerId, projectId))

export const useOverrideComponentsForPositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<IComponent, OverrideComponentData>(
    projectPositionEndpoints.overrideComponetsForPositions(customerId, projectId),
  )

export const useGetProjectPosition = (
  customerId: EntityId,
  projectId: EntityId,
  positionId: EntityId,
  enabled: boolean,
) => {
  return useGet<IPosition>(
    projectPositionEndpoints.getProjectPosition(customerId, projectId, positionId),
    {},
    {
      enabled: !!customerId && !!projectId && !!positionId && enabled,
      queryKey: projectPositionQueryKeys.GET_POSITION(customerId, projectId, positionId),
    },
  )
}

export const useGetMultipleProjectPositions = (
  customerId: EntityId,
  projectId: EntityId,
  positionIds: EntityId[],
  enabled: boolean,
) => {
  return useGet<IPositionWithDrawing[]>(
    projectPositionEndpoints.getMultipleProjectPositions(customerId, projectId, positionIds),
    {},
    {
      enabled: !!customerId && !!projectId && positionIds.length > 0 && enabled,
      queryKey: projectPositionQueryKeys.GET_MULTIPLE_POSITIONS(customerId, projectId, positionIds),
    },
  )
}

export const useGetComponentsByProductId = (
  customerId: EntityId,
  projectId: EntityId,
  positionIds: EntityId[],
  productId: EntityId,
  positionNumber: number,
  enabled: boolean,
) => {
  return useGet<IComponent[]>(
    projectPositionEndpoints.getComponentsByProductId(customerId, projectId, positionIds),
    { productId, positionNumber },
    {
      enabled: !!customerId && !!projectId && positionIds.length > 0 && enabled,
      queryKey: projectPositionQueryKeys.GET_COMPONENTS_BY_PRODUCT_ID(
        customerId,
        projectId,
        positionIds,
        productId,
        positionNumber,
      ),
    },
  )
}

export const useGetComponentsForSinglePosition = (
  customerId: EntityId,
  projectId: EntityId,
  positionId: EntityId,
  enabled: boolean,
) => {
  return useGet<IComponent[]>(
    projectPositionEndpoints.getComponentsForSinglePosition(customerId, projectId, positionId),
    {},
    {
      enabled: !!customerId && !!projectId && !!positionId && enabled,
      queryKey: [projectComponentEndpoints.getComponents(customerId, positionId), 'with-prices'],
    },
  )
}

export const useGetComponentsForPositionIds = (
  customerId: EntityId,
  projectId: EntityId,
  groupPositionIds: EntityId[],
  positionIds: EntityId[],
  enabled: boolean,
) => {
  return useGet<IComponent[]>(
    projectPositionEndpoints.getComponentsForPositionIds(customerId, projectId, positionIds),
    {},
    {
      enabled: !!customerId && !!projectId && positionIds.length > 0 && enabled,
      queryKey: projectPositionQueryKeys.GET_COMPONENTS_FOR_SELECTED_POSITIONS(
        customerId,
        projectId,
        groupPositionIds,
        positionIds,
      ),
    },
  )
}

export const useAddComponentNotes = (customerId: EntityId, positionId: EntityId) =>
  usePost<IComponent, IComponentNote>(projectPositionEndpoints.createComponentNotes(customerId, positionId))

export const useUpdateProjectPositionMinimalData = (customerId: EntityId, projectId: EntityId, id: EntityId) =>
  usePut<ICagePositionMinimalData, ICagePositionMinimalData>(
    projectPositionEndpoints.updatePositionMinimalData(customerId, projectId, id),
  )

export const useUpdateGroupedPositionComponents = (customerId: EntityId, projectId: EntityId) =>
  usePost<IComponent[], IUpdateGroupedPositionComponents>(
    projectPositionEndpoints.updateGroupedPositionComponents(customerId, projectId),
  )

export const useUpdatePositionComponents = (customerId: EntityId, projectId: EntityId, positionId: EntityId) =>
  usePost<IComponent[], IPositionComponentUpdateData>(
    projectPositionEndpoints.updatePositionComponents(customerId, projectId, positionId),
  )

export const useDeleteProjectPosition = (customerId: EntityId, projectId: EntityId) =>
  useDelete(projectPositionEndpoints.deleteProjectPosition(customerId, projectId))

export const useUpdateComponentOnDrag = (customerId: EntityId, projectId: EntityId, positionId: EntityId) =>
  usePost<IComponent, IUpdateProjectComponentDrawingNumber>(
    projectPositionEndpoints.updateComponentOnDrag(customerId, projectId, positionId),
  )

export const useBulkUpdateComponentsOnDrag = (customerId: EntityId, projectId: EntityId) =>
  usePost<IComponent[], IBulkUpdateProjectComponentDrawingNumber>(
    projectPositionEndpoints.bulkUpdateComponentsOnDrag(customerId, projectId),
  )

export const useAddPositionDrawingGroup = (customerId: EntityId, projectId: EntityId) =>
  usePost<PositionDrawingGroup, createPositionDrawingGroup>(
    projectPositionEndpoints.addPositionDrawingGroup(customerId, projectId),
  )

export const useGetPositionDrawingGroups = (
  customerId: EntityId,
  projectId: EntityId,
  positionDrawingId: EntityId,
  enabled: boolean,
) => {
  return useGet<PositionsWithGroups>(
    projectPositionEndpoints.getPositionDrawingGroups(customerId, projectId, positionDrawingId),
    {},
    {
      enabled,
      queryKey: projectPositionQueryKeys.GET_POSITION_DRAWING_GROUPS(customerId, projectId, positionDrawingId),
    },
  )
}

export const useUnAssignPositionsFromGroup = (customerId: EntityId, projectId: EntityId) =>
  usePost<void, { positionIds: EntityId[]; groupId: EntityId }>(
    projectPositionEndpoints.unAssignPositionsFromGroup(customerId, projectId),
  )

export const useUpdatePositionGroupName = (customerId: EntityId, projectId: EntityId) =>
  usePost<void, { groupId: EntityId; name: string }>(
    projectPositionEndpoints.updatePositionGroupName(customerId, projectId),
  )

export const useAddNoteToGroup = (customerId: EntityId, projectId: EntityId) =>
  usePost<void, { groupId: EntityId; note: string }>(projectPositionEndpoints.addNoteToGroup(customerId, projectId))

export const useAddPositionsToGroup = (customerId: EntityId, projectId: EntityId) =>
  usePost<void, { groupId: EntityId; positionIds: EntityId[] }>(
    projectPositionEndpoints.addPostitionsToGroup(customerId, projectId),
  )

export const useChangePositionDrawing = (customerId: EntityId, projectId: EntityId) =>
  usePut<IPositionWithDrawing[], { positionIds: EntityId[]; positionDrawingId: EntityId }>(
    projectPositionEndpoints.changePositionDrawing(customerId, projectId),
  )

export const useGetAllPositionDrawingGroups = (customerId: EntityId, projectId: EntityId, enabled: boolean) => {
  return useGet<PositionDrawingGroup[]>(
    projectPositionEndpoints.getAllPositionDrawingGroups(customerId, projectId),
    {},
    {
      enabled: !!customerId && !!projectId && enabled,
      queryKey: projectPositionQueryKeys.GET_ALL_POSITION_DRAWING_GROUPS(customerId, projectId),
    },
  )
}

export const useGetPositionsGroupedByDrawingGroups = (customerId: EntityId, projectId: EntityId, enabled: boolean) => {
  return useGet<DrawingWithGroups[]>(
    projectPositionEndpoints.getPositionsGroupedByDrawingGroups(customerId, projectId),
    {},
    {
      enabled: !!customerId && !!projectId && enabled,
      queryKey: projectPositionQueryKeys.GET_POSITIONS_GROUPED_BY_DRAWING_GROUPS(customerId, projectId),
    },
  )
}

export const useConfigurePositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<void, IConfigurePositionsData>(projectPositionEndpoints.configurePositions(customerId, projectId))

export const useDeletePositionDrawingGroup = (customerId: EntityId, projectId: EntityId) =>
  useDelete(projectPositionEndpoints.deletePositionDrawingGroup(customerId, projectId))

export const useBulkConfigurePositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<void, IConfigurePositionsData[]>(projectPositionEndpoints.bulkConfigurePositions(customerId, projectId))

export const useGetProjectComponentCount = (customerId: EntityId, projectId: EntityId, enabled: boolean) => {
  return useGet<ComponentCount[]>(
    projectPositionEndpoints.getProjectComponentCount(customerId, projectId),
    {},
    {
      enabled: !!customerId && !!projectId && enabled,
      queryKey: projectPositionEndpoints.getProjectComponentCount(customerId, projectId),
    },
  )
}

export const useGetProjectWorkList = (customerId: EntityId, projectId: EntityId, enabled: boolean) => {
  return useGet<IWorkWithPosition[]>(
    projectPositionEndpoints.getProjectWorkList(customerId, projectId),
    {},
    {
      enabled: !!customerId && !!projectId && enabled,
      queryKey: projectPositionEndpoints.getProjectWorkList(customerId, projectId),
    },
  )
}

export const useMarkPositionsAsCompleted = (customerId: EntityId, projectId: EntityId) =>
  usePost<IApiResult, { positionIds: EntityId[] }>(projectPositionEndpoints.markAsCompleted(customerId, projectId))

export const useMarkPositionsAsIncomplete = (customerId: EntityId, projectId: EntityId) =>
  usePost<IApiResult, { positionIds: EntityId[] }>(projectPositionEndpoints.markAsIncomplete(customerId, projectId))

export const useMarkComponentAsReused = (customerId: EntityId, projectId: EntityId) =>
  usePost<IApiResult, { componentId: EntityId }>(projectPositionEndpoints.markComponentAsReused(customerId, projectId))

export const useConfirmProjectPositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<void, IConfirmOlexPositions>(projectPositionEndpoints.confirmProjectPositions(customerId, projectId))
